'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, t } from '@lingui/macro';
import { Button, Stack } from '@mui/material';
import { TextFieldInput } from '@prismo-io/design-system';
import type { FC } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSendResetPasswordForEmail } from '../../../hooks/use-send-reset-password';
import {
  SendPasswordResetSchema,
  type SendPasswordResetSchemaT,
} from './schema';

type SendResetPasswordProps = {
  onComplete?: SubmitHandler<SendPasswordResetSchemaT>;
  defaultValues?: Partial<SendPasswordResetSchemaT>;
};

const DEFAULT_VALUES: SendPasswordResetSchemaT = {
  email: '',
};

export const SendPasswordResetForm: FC<SendResetPasswordProps> = (props) => {
  const { onComplete, defaultValues = {} } = props;

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
  } = useForm<SendPasswordResetSchemaT>({
    mode: 'all',
    resolver: zodResolver(SendPasswordResetSchema()),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { trigger: sendResetPassword } = useSendResetPasswordForEmail();

  const onSubmit: SubmitHandler<SendPasswordResetSchemaT> = (data) => {
    sendResetPassword(
      {
        email: data.email,
      },
      {
        onSuccess: () => {
          if (onComplete) {
            onComplete(data);
          }
        },
      }
    );
  };

  return (
    <Stack
      spacing={2}
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      id="send-reset-password"
    >
      <TextFieldInput
        control={control}
        disabled={isSubmitting || isSubmitSuccessful}
        name="email"
        label={t`Email`}
        type="email"
        autoComplete="email"
        size="small"
        required
      />
      <Button
        type="submit"
        disabled={!isValid || isSubmitSuccessful}
        variant="contained"
        color="primary"
        form="send-reset-password"
      >
        <Trans>Demande réinitialisation du mot de passe</Trans>
      </Button>
    </Stack>
  );
};
